// Venmo statement CSV. Mirrors load_venmo_csv.
//
// Venmo prepends 2-3 metadata lines before the header. findHeaderIndex
// against "Datetime" + "Amount (total)" walks past the preamble.
//
// Sign: Amount (total) is signed (negative = sent, positive = received).
// Flip to project convention (income negative, expense positive).
// Counterparty heuristic: when outflow (we sent), the recipient is the
// "To" field; when inflow, the sender is the "From" field.

import {
  type Transaction,
  newTransaction,
  makeId,
  parseAmount,
  parseDate,
  normalizeVendor,
  isInterPortalTransfer,
} from "../common.js";
import { parseCsv, toDictRows, findHeaderIndex } from "../csv.js";

export async function loadVenmoCsv(
  text: string,
  filename: string,
): Promise<Transaction[]> {
  const rows = parseCsv(text);
  const headerIdx = findHeaderIndex(rows, ["Datetime", "Amount (total)"]);
  if (headerIdx < 0 || headerIdx >= rows.length - 1) return [];
  const dicts = toDictRows(rows[headerIdx], rows.slice(headerIdx + 1));

  const out: Transaction[] = [];
  for (const row of dicts) {
    const datetimeRaw = (row["Datetime"] || "").trim();
    if (!datetimeRaw) continue;
    const dateOnly = datetimeRaw.split("T")[0];
    const date = parseDate(dateOnly);
    if (!date) continue;
    const amtRaw = row["Amount (total)"] || "0";
    const amount = -parseAmount(amtRaw);
    if (amount === 0) continue;
    const txType = (row["Type"] || "").trim().toLowerCase();
    const note = (row["Note"] || "").trim();
    const fromWho = (row["From"] || "").trim();
    const toWho = (row["To"] || "").trim();
    const isOutflow = parseAmount(amtRaw) < 0;
    const counterparty = isOutflow ? toWho : fromWho;
    const description = [counterparty, note]
      .filter((p) => p.length > 0)
      .join(" — ") || txType || "Venmo transaction";
    if (isInterPortalTransfer(description)) continue;

    const id = await makeId("venmo", "Venmo", date, amount, description);
    out.push(
      newTransaction({
        id,
        date,
        vendor_raw: description,
        vendor_key: normalizeVendor(description) || "venmo",
        amount: Math.round(amount * 100) / 100,
        portal: "venmo",
        account: "Venmo",
        account_kind: "debit",
        source_file: filename,
        raw: row,
      }),
    );
  }
  return out;
}
